import type { CSSProperties, ReactElement } from "react";
import { X } from "lucide-react";
import { Button, Stack, Text } from "@ux/primitives";
import { palette, radius, space } from "@ux/tokens";
import {
    AssetForm,
    type AssetFormWorkspaceContext,
} from "../../admin/assets/AssetForm";
import type { AssetUpsertPayload, AssetView } from "../../admin/assets/types";

export interface AddAssetDialogProps {
    readonly open: boolean;
    readonly workspaceContext: AssetFormWorkspaceContext;
    readonly onSave: (payload: AssetUpsertPayload) => void;
    readonly onCancel: () => void;
    readonly onReassignExisting: (existing: AssetView) => void;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
}

/**
 * Modal host for the shared {@link AssetForm} create flow, scoped to a
 * floorplan desk/room. Mirrors the workspaces page `add-asset` mode
 * without navigating away from the map.
 */
export function AddAssetDialog({
    open,
    workspaceContext,
    onSave,
    onCancel,
    onReassignExisting,
    isSaving,
    errorMessage,
}: AddAssetDialogProps): ReactElement | null {
    if (!open) return null;

    return (
        <div style={backdropStyle} role="presentation" onMouseDown={onCancel}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Add asset to workspace"
                style={dialogStyle}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div style={headerStyle}>
                    <Stack gap={1} grow>
                        <Text role="eyebrow">Add asset</Text>
                        <Text role="heading" as="h2">
                            {workspaceContext.name} · {workspaceContext.type}
                        </Text>
                    </Stack>
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Close"
                        onClick={onCancel}
                        leadingIcon={<X size={16} />}
                    >
                        Close
                    </Button>
                </div>
                <div style={bodyStyle}>
                    <AssetForm
                        mode={{ kind: "create" }}
                        workspaceContext={workspaceContext}
                        onSave={onSave}
                        onCancel={onCancel}
                        onRequestDelete={() => undefined}
                        onRequestRestore={() => undefined}
                        onReassignExisting={onReassignExisting}
                        isSaving={isSaving}
                        errorMessage={errorMessage}
                    />
                </div>
            </div>
        </div>
    );
}

const backdropStyle: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "rgba(0, 0, 0, 0.55)",
    display: "grid",
    placeItems: "center",
    padding: space[4],
};

const dialogStyle: CSSProperties = {
    width: "min(720px, 100%)",
    maxHeight: "min(900px, 92vh)",
    display: "flex",
    flexDirection: "column",
    background: palette.canvas,
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: radius.lg,
    boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
    overflow: "hidden",
};

const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: space[3],
    padding: `${space[3]} ${space[4]}`,
    borderBottom: `1px solid ${palette.border}`,
    background: palette.surface,
};

const bodyStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
};
